/**
 * GatewayContentFiltering -- Gateway > Configure > Content Filtering.
 *
 * A CURATED FRONT END over one field that already ships end to end: a LAN network's
 * DHCP `use-dns` list. There is no new backend intent and no new endpoint here. Per
 * downstream network the operator picks a profile (off / family / malware / custom),
 * and the write goes through the SAME propose-then-confirm path as Gateway > DHCP --
 * the `dhcp.scope` intent, the shared GatewayReviewDialog (components/gateway/
 * GatewayChrome) -- never a second write path. The profiles live in
 * lib/dnsFilterProfiles (mirroring services/dns_filter_catalog.py).
 *
 * WHAT THIS IS, STATED PLAINLY ON THE PAGE (it is easy to oversell):
 *   - DNS-LAYER filtering, not URL/category filtering. It works by handing clients a
 *     filtering resolver over DHCP. A client that ignores that resolver, hard-codes
 *     its own, or uses encrypted DNS (DoH/DoT) is NOT covered.
 *   - Empty `use-dns` means "clients use the gateway as their resolver", NOT
 *     "filtering off in general". The picker's "Off" option and the copy keep that
 *     distinction; they are never conflated.
 *
 * SAFETY: a `dhcp.scope` write re-sends the scope's whole DHCP block, and the server's
 * validator DEFAULTS the range if it is omitted. So every write here re-sends the
 * network's existing lease range / count / time unchanged and only swaps `use-dns` --
 * changing the resolver must never move the DHCP pool. Content filtering is offered
 * only where the network already runs a DHCP server (that is where `use-dns` lives).
 */
import { useEffect, useMemo, useState } from "react";
import { useDevices, useGatewayDhcp } from "@/lib/api/hooks";
import type { GatewayChangeSpec, GatewayDhcpScope, GatewayPlan } from "@/types/domain";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import {
  DeltaChip,
  GatewayPicker,
  GatewayReviewDialog,
  INPUT,
  NotEditableBanner,
  type PendingGatewayChange,
} from "@/components/gateway/GatewayChrome";
import {
  DNS_FILTER_PROFILES,
  dnsForProfile,
  matchDnsFilterProfile,
  type DnsFilterProfileId,
} from "@/lib/dnsFilterProfiles";

/** Client-side IPv4 check for the custom field. The server (`_clean_use_dns`) is the authority. */
function isIpv4(value: string): boolean {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(value.trim());
  if (!m) return false;
  return [m[1], m[2], m[3], m[4]].every((o) => {
    const n = Number(o);
    return n >= 0 && n <= 255 && String(n) === o;
  });
}

/** Split a free-text DNS field into trimmed, non-empty tokens (comma/space separated). */
function parseDnsList(text: string): string[] {
  return text.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
}

/** Sort-insensitive equality for two IP lists (order of resolvers is not meaningful). */
function sameList(a: string[], b: string[]): boolean {
  return [...a].sort().join(",") === [...b].sort().join(",");
}

/**
 * Build the `dhcp.scope` spec for a use-dns change. CRITICAL: re-send the existing
 * range/count/time so swapping the resolver never resets the DHCP pool (the server
 * defaults any omitted field). Only `use_dns` differs from what the gateway holds.
 */
function scopeSpec(scope: GatewayDhcpScope, useDns: string[]): GatewayChangeSpec {
  const d = scope.dhcp;
  return {
    interface: scope.name ?? "",
    dhcp_enabled: true,
    dhcp_lease_first: d?.lease_first ?? undefined,
    dhcp_lease_count: d?.lease_count ?? undefined,
    dhcp_lease_time: d?.lease_time ?? undefined,
    use_dns: useDns,
  };
}

export default function GatewayContentFiltering() {
  // Gateways only, one at a time -- content filtering is a property of a box's LAN scopes.
  const { data: devices, isLoading: devicesLoading, error: devicesError } = useDevices({
    kind: "gateway",
    limit: 100,
  });
  const gateways = devices?.items ?? [];
  const [serial, setSerial] = useState<string | null>(null);
  useEffect(() => {
    const first = gateways[0];
    if (!serial && first) setSerial(first.serial);
  }, [gateways, serial]);

  const { data, isLoading, error } = useGatewayDhcp(serial ?? undefined);
  const [pending, setPending] = useState<PendingGatewayChange | null>(null);

  // Switching gateways must not carry a half-open review across boxes.
  useEffect(() => setPending(null), [serial]);

  const editable = !!data?.editable;

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader
          title="Content Filtering"
          subtitle="Point each LAN network's clients at a filtering DNS resolver"
        />
        <div className="flex items-center gap-2">
          <GatewayPicker
            serial={serial}
            gateways={gateways.map((g) => ({ serial: g.serial, name: g.name }))}
            loading={devicesLoading}
            onChange={setSerial}
          />
        </div>
      </div>

      {/* The point of the page, said up front: this is DNS steering, not category blocking. */}
      <DnsLayerNotice />

      {devicesError && <ErrorBanner error={devicesError} />}
      {error && <ErrorBanner error={error} />}

      {!devicesLoading && gateways.length === 0 && (
        <EmptyState
          title="No gateways in this scope"
          hint="This screen steers DNS on a uCentral whitebox gateway. Onboard one, or switch to a site that has one."
        />
      )}

      {isLoading && serial && <div className="mt-3 text-sm text-ink-muted">Loading networks…</div>}

      {data && (
        <div className="mt-3 flex flex-col gap-3">
          {!editable && data.reason && (
            <NotEditableBanner title="This gateway cannot be edited yet" reason={data.reason} />
          )}

          {data.scopes.map((scope) => (
            <ScopeFilterRow
              // Re-seed the row from the server after a successful apply (use-dns changed).
              key={`${scope.location}:${JSON.stringify(scope.dhcp?.use_dns ?? [])}`}
              scope={scope}
              editable={editable}
              onReview={setPending}
            />
          ))}

          {data.scopes.length === 0 && editable && (
            <EmptyState
              title="This gateway has no LAN networks"
              hint="Content filtering steers a network's DHCP DNS. Add a LAN network under Gateway → Site VLANs first."
            />
          )}

          <p className="text-[0.6875rem] text-ink-subtle">
            {data.config_uuid != null ? `Config #${data.config_uuid} · ` : ""}
            Every change is previewed before it is pushed, and a push replaces the gateway's whole
            configuration. Only the DNS servers change here — the DHCP range, lease time and fixed IP
            assignments are re-sent unchanged.
          </p>
        </div>
      )}

      {pending && serial && (
        <GatewayReviewDialog
          serial={serial}
          change={pending}
          onClose={() => setPending(null)}
          onApplied={() => setPending(null)}
          delta={(plan) => <ContentFilterDelta plan={plan} />}
          outcomeText={(r) =>
            `Read-back verdict: ${r.verdict ?? "pending"}. The DNS servers are written into the ` +
            `gateway's configuration but are not echoed in its state document, so no read-back can ` +
            `confirm clients are using them — only that the gateway accepted the configuration. ` +
            `This steers DNS only: a client using encrypted DNS (DoH/DoT) or a hard-coded resolver ` +
            `is not affected.`
          }
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The unmissable caveat. Content filtering is easy to oversell; this is the honesty gate.
 * ------------------------------------------------------------------ */

function DnsLayerNotice() {
  return (
    <div role="note" className="md-card mt-3 border-warn/40 bg-warn-soft p-4 text-sm">
      <div className="font-medium text-ink">This is DNS-layer filtering, not URL/category filtering</div>
      <p className="mt-1 text-ink-muted">
        It works by handing a network's clients a filtering DNS resolver over DHCP. A client that
        ignores this DNS server, hard-codes its own, or uses encrypted DNS (DoH/DoT) is not covered.
        True category/URL filtering needs a categorisation feed and on-device enforcement — neither
        exists on this gateway yet.
      </p>
      <p className="mt-1 text-ink-muted">
        <span className="font-medium text-ink">Off</span> means clients use the gateway as their
        resolver — it does not switch filtering off across the site, it just sets no explicit
        filtering resolver on this network.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * One LAN network's content-filtering control.
 * ------------------------------------------------------------------ */

function ScopeFilterRow({
  scope,
  editable,
  onReview,
}: {
  scope: GatewayDhcpScope;
  editable: boolean;
  onReview: (change: PendingGatewayChange) => void;
}) {
  // The DHCP server is where `use-dns` lives; without it there is nothing to steer.
  const dhcpOn = !!scope.dhcp && scope.enabled;
  const currentUseDns = useMemo(() => scope.dhcp?.use_dns ?? [], [scope.dhcp]);
  const current = matchDnsFilterProfile(currentUseDns);

  const [selection, setSelection] = useState<DnsFilterProfileId>(current);
  // The custom field seeds from the current list only when the network is already on a
  // hand-set (custom) resolver; otherwise it starts empty for the operator to fill.
  const [customText, setCustomText] = useState(current === "custom" ? currentUseDns.join(", ") : "");

  const customList = parseDnsList(customText);
  const writeUseDns = selection === "custom" ? customList : (dnsForProfile(selection) ?? []);

  const problem =
    selection === "custom"
      ? customList.length === 0
        ? "Enter at least one DNS server, or choose Off to use the gateway as resolver."
        : customList.some((d) => !isIpv4(d))
          ? "DNS servers must be IPv4 addresses, not hostnames."
          : null
      : null;

  const changed = !problem && !sameList(writeUseDns, currentUseDns);
  // Reset is offered whenever the form differs from what the gateway holds, even if that
  // difference is not yet valid (a half-typed custom list the operator wants to abandon).
  const dirty =
    selection !== current || (selection === "custom" && !sameList(customList, currentUseDns));

  const reset = () => {
    setSelection(current);
    setCustomText(current === "custom" ? currentUseDns.join(", ") : "");
  };

  const review = () =>
    onReview({
      intent: "dhcp.scope",
      label: "Set content filtering",
      spec: scopeSpec(scope, writeUseDns),
    });

  const selected = DNS_FILTER_PROFILES.find((p) => p.id === selection);

  return (
    <section className="md-card overflow-hidden" aria-label={`Content filtering on ${scope.name ?? "network"}`}>
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
        <h2 className="text-base font-semibold text-ink">{scope.name ?? "—"}</h2>
        <span className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-[0.6875rem] text-ink-muted">
          {scope.vlan != null ? `VLAN ${scope.vlan}` : "untagged"}
        </span>
        <span className="font-mono text-xs text-ink-muted">{scope.subnet ?? "no subnet"}</span>
        <span className="ml-auto text-xs text-ink-subtle">
          Now: <span className="font-medium text-ink-muted">{DNS_FILTER_PROFILES.find((p) => p.id === current)?.label ?? current}</span>
          {currentUseDns.length > 0 && <span className="ml-1 font-mono">({currentUseDns.join(", ")})</span>}
        </span>
      </header>

      <div className="px-4 py-3">
        {!dhcpOn ? (
          <p className="text-sm text-ink-muted">
            This network has no DHCP server, so it hands out no DNS servers. Turn its DHCP server on
            under <span className="font-medium text-ink">Gateway → DHCP</span> first, then choose a
            filtering profile here.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Filtering profile</span>
              <select
                value={selection}
                disabled={!editable}
                aria-label={`Filtering profile for ${scope.name ?? "network"}`}
                onChange={(e) => setSelection(e.target.value as DnsFilterProfileId)}
                className={`${INPUT} min-h-touch w-full max-w-sm`}
              >
                {DNS_FILTER_PROFILES.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
              {selected && <span className="text-xs text-ink-subtle">{selected.hint}</span>}
            </label>

            {selection !== "custom" && selected?.dns && selected.dns.length > 0 && (
              <div className="font-mono text-xs text-ink-muted">
                Resolvers: {selected.dns.join(", ")}
              </div>
            )}

            {selection === "custom" && (
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">DNS servers</span>
                <input
                  value={customText}
                  disabled={!editable}
                  onChange={(e) => setCustomText(e.target.value)}
                  placeholder="185.228.168.168, 185.228.169.168"
                  aria-label="Custom DNS server addresses"
                  className={`${INPUT} min-h-touch w-full max-w-sm font-mono`}
                />
              </label>
            )}

            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line/60 pt-3">
              {problem && <span className="mr-auto text-xs text-critical">{problem}</span>}
              <Button variant="ghost" disabled={!dirty} onClick={reset}>
                Reset
              </Button>
              <RoleGate min="org_admin" mode="hide">
                <Button variant="primary" disabled={!editable || !changed} onClick={review}>
                  Review changes
                </Button>
              </RoleGate>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Review delta -- per network, DNS resolver before/after (and its profile).
 * ------------------------------------------------------------------ */

function ContentFilterDelta({ plan }: { plan: GatewayPlan }) {
  const before = plan.dhcp_before?.scopes ?? [];
  const after = plan.dhcp_after?.scopes ?? [];
  const key = (s: GatewayDhcpScope) => s.name ?? s.location;
  const beforeBy = new Map(before.map((s) => [key(s), s]));
  const afterBy = new Map(after.map((s) => [key(s), s]));
  const names = Array.from(new Set([...beforeBy.keys(), ...afterBy.keys()]));

  const line = (s?: GatewayDhcpScope) => {
    const list = s?.dhcp?.use_dns ?? [];
    const profile = matchDnsFilterProfile(list);
    const label = DNS_FILTER_PROFILES.find((p) => p.id === profile)?.label ?? profile;
    return list.length ? `${label} — ${list.join(", ")}` : `${label} (clients use the gateway)`;
  };

  const items = names
    .filter((n) => {
      const b = beforeBy.get(n)?.dhcp?.use_dns ?? [];
      const a = afterBy.get(n)?.dhcp?.use_dns ?? [];
      return !sameList(b, a);
    })
    .map((n) => {
      const b = beforeBy.get(n);
      const a = afterBy.get(n);
      return (
        <li key={`dns-${n}`} className="px-4 py-2 text-xs">
          <div className="font-medium text-ink">
            {n} DNS <DeltaChip kind={!b ? "added" : !a ? "removed" : "changed"} />
          </div>
          {b && <div className="font-mono text-ink-subtle">before: {line(b)}</div>}
          {a && <div className="font-mono text-ink-muted">after: {line(a)}</div>}
        </li>
      );
    });

  return (
    <div className="md-card overflow-hidden">
      <div className="border-b border-line px-4 py-2 text-sm font-medium text-ink">
        {items.length} DNS change{items.length === 1 ? "" : "s"}
      </div>
      <ul className="divide-y divide-line">
        {items.length ? (
          items
        ) : (
          <li className="px-4 py-3 text-xs text-ink-muted">
            No DNS changes — see the configuration diff below.
          </li>
        )}
      </ul>
    </div>
  );
}
